/*
 * IndianJewellery.com — Account Cart Sync (explicit-removal patch)
 *
 * Keeps Shopify's local cart immediate. A separate customer metafield stores
 * an account snapshot for other devices. Uses the existing wishlist OAuth
 * session bridge, but never reads or writes wishlist data.
 *
 * Conflict rules:
 * - First merge: preserve active account and local lines; max quantity for
 *   matching lines.
 * - Later changes: present local quantity changes are reconciled against the
 *   account baseline. Absence from one browser is NOT a removal.
 * - Only a confirmed cart.js user removal creates a tombstone.
 */
(() => {
  'use strict';
  if (window.IJCartAccountSync) return;

  const CFG = window.IJCartAccountConfig || window.IJWishlistAccountConfig || {};
  const FIELD = { namespace: 'ij_cart', key: 'saved_cart' };
  const VERSION = 2;
  // New baseline generation makes the first patched sync a non-destructive
  // merge. Old baselines may contain false removals from the previous logic.
  const BASELINE_PREFIX = 'ij_cart_account_baseline_v2_';
  const REMOVAL_PREFIX = 'ij_cart_explicit_removals_v1_';
  const AUTH_ATTEMPT = 'ij_cart_silent_auth_attempt_v1';
  const POLL_MS = 5000;
  const MAX_LINES = 300;
  const MAX_BYTES = 90000;
  let working = false;
  let syncInFlight = null;
  let muted = false;
  let timer = 0;
  let poller = 0;
  let applyRemoteQueued = false;
  let accountWasAuthenticated = false;
  let syncAgain = false;
  let authAttempted = false;
  let cartMutationVersion = 0;
  let pendingCartMutations = 0;

  const ajaxRoute = (route, fallback) => {
    const path = String(route || fallback).replace(/\/$/, '');
    return path.endsWith('.js') ? path : `${path}.js`;
  };
  const CART_URL = ajaxRoute(window.routes?.cart_url, '/cart');
  const CART_ADD_URL = ajaxRoute(window.routes?.cart_add_url, '/cart/add');
  const CART_CHANGE_URL = ajaxRoute(window.routes?.cart_change_url, '/cart/change');

  const parse = (value, fallback) => {
    try { return JSON.parse(value); } catch { return fallback; }
  };
  const accountApi = () => window.IJWishlistAccountSync;
  const status = () => {
    try { return accountApi()?.getStatus?.() || { authenticated: false, ssoVerified: false }; }
    catch { return { authenticated: false }; }
  };
  const validSession = () => {
    const current = status();
    return !!current.authenticated && Number(current.expiresAt) > Date.now() + 30000;
  };
  const expectedCustomerId = () => String(CFG.customerId || '').match(/\d+$/)?.[0] || '';
  const ownerKey = owner => BASELINE_PREFIX + String(owner || '').replace(/[^a-zA-Z0-9_-]/g, '_');
  const clone = value => JSON.parse(JSON.stringify(value));
  const validVariant = value => /^\d{1,20}$/.test(String(value || ''));
  const sortedObject = value => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return Object.fromEntries(Object.keys(value).sort().map(key => [key, value[key]]));
  };
  const identityFor = item => JSON.stringify({
    variant_id: String(item.variant_id),
    properties: sortedObject(item.properties),
    selling_plan: item.selling_plan == null ? null : String(item.selling_plan),
  });
  const sameLine = (a, b) => {
    if (!a || !b) return a === b;
    return Number(a.quantity) === Number(b.quantity) &&
      identityFor(a) === identityFor(b);
  };
  const cartLine = item => {
    const variantId = String(item.variant_id || item.id || '').replace(/^gid:\/\/shopify\/ProductVariant\//, '');
    if (!validVariant(variantId)) return null;
    const line = {
      variant_id: variantId,
      quantity: Math.max(0, Math.floor(Number(item.quantity) || 0)),
      properties: sortedObject(item.properties),
      selling_plan: item.selling_plan == null
        ? (item.selling_plan_allocation?.selling_plan?.id == null
          ? null : String(item.selling_plan_allocation.selling_plan.id))
        : String(item.selling_plan),
      updated_at: 0,
    };
    line.identity = identityFor(line);
    return line;
  };
  const indexLines = lines => {
    const map = new Map();
    for (const raw of lines || []) {
      if (!raw || !validVariant(raw.variant_id)) continue;
      const line = {
        variant_id: String(raw.variant_id),
        quantity: Math.max(0, Math.floor(Number(raw.quantity) || 0)),
        properties: sortedObject(raw.properties),
        selling_plan: raw.selling_plan == null ? null : String(raw.selling_plan),
        updated_at: Math.max(0, Number(raw.updated_at) || 0),
      };
      line.identity = identityFor(line);
      const prior = map.get(line.identity);
      if (!prior || line.updated_at >= prior.updated_at) map.set(line.identity, line);
    }
    return map;
  };
  const normalizeDocument = value => {
    const doc = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    const lines = [...indexLines(doc.lines).values()].sort((a, b) => {
      if ((a.quantity > 0) !== (b.quantity > 0)) return a.quantity > 0 ? -1 : 1;
      return b.updated_at - a.updated_at;
    }).slice(0, MAX_LINES);
    return {
      version: VERSION,
      updated_at: Math.max(0, Number(doc.updated_at) || 0),
      lines,
    };
  };
  const documentValue = doc => JSON.stringify({
    version: VERSION,
    updated_at: doc.updated_at,
    lines: doc.lines.map(({ identity, key, ...line }) => line),
  });

  async function request(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...options,
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(body.description || body.message || `Cart request failed (${response.status})`);
    }
    return body;
  }
  const postCart = (path, payload) => request(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify(payload),
  });
  async function readCart() {
    const cart = await request(CART_URL, { headers: { 'Accept': 'application/json' } });
    const lines = new Map();
    for (const raw of cart.items || []) {
      const line = cartLine(raw);
      if (line) {
        const prior = lines.get(line.identity);
        if (!prior) lines.set(line.identity, { ...line, key: raw.key });
        else prior.quantity += line.quantity;
      }
    }
    return { cart, lines };
  }
  async function readAccount() {
    const api = accountApi();
    if (!api?.graphql || !status().authenticated) return null;
    const query = `query IJCartSyncRead {
      customer {
        id
        metafield(namespace: "${FIELD.namespace}", key: "${FIELD.key}") {
          type value compareDigest
        }
      }
    }`;
    const data = await api.graphql(query);
    const customer = data?.customer;
    if (!customer?.id) throw new Error('Customer account could not be confirmed.');
    const expectedId = expectedCustomerId();
    const actualId = String(customer.id).match(/\d+$/)?.[0] || '';
    if (expectedId && actualId !== expectedId) {
      throw new Error('The signed-in storefront account does not match the account-sync session.');
    }
    sessionStorage.removeItem(AUTH_ATTEMPT);
    const metafield = customer.metafield;
    if (metafield && metafield.type !== 'json') throw new Error('The saved cart metafield must use JSON type.');
    const parsed = metafield ? parse(metafield.value, null) : null;
    if (metafield && !parsed) throw new Error('The saved cart data is not valid JSON; the local cart was left unchanged.');
    return {
      owner: customer.id,
      digest: metafield?.compareDigest ?? null,
      document: normalizeDocument(parsed),
    };
  }
  async function writeAccount(owner, digest, document) {
    const value = documentValue(document);
    if (new TextEncoder().encode(value).length > MAX_BYTES) {
      throw new Error('The saved cart is too large to sync; the local cart was left unchanged.');
    }
    const mutation = `mutation IJCartSyncWrite($metafields: [MetafieldsSetInput!]!) {
      metafieldsSet(metafields: $metafields) {
        metafields { namespace key value compareDigest }
        userErrors { field code message }
      }
    }`;
    const data = await accountApi().graphql(mutation, {
      metafields: [{
        ownerId: owner,
        namespace: FIELD.namespace,
        key: FIELD.key,
        type: 'json',
        value,
        compareDigest: digest,
      }],
    });
    const result = data?.metafieldsSet;
    if (result?.userErrors?.length) {
      const errors = result.userErrors;
      const conflict = errors.some(error =>
  /compare.?digest|stale|conflict/i.test(`${error.code} ${error.message}`)
);
      const err = new Error(errors.map(error => error.message).join('; '));
      err.conflict = conflict;
      throw err;
    }
    const saved = result?.metafields?.[0];
    if (!saved) throw new Error('Shopify did not confirm the saved cart update.');
    return { digest: saved.compareDigest ?? null, document: normalizeDocument(parse(saved.value, document)) };
  }
  const baselineRead = owner => normalizeDocument(parse(localStorage.getItem(ownerKey(owner)) || '', null));
  const baselineWrite = (owner, document) => localStorage.setItem(ownerKey(owner), JSON.stringify(document));
  const removalKey = owner => REMOVAL_PREFIX + String(owner || '').replace(/[^a-zA-Z0-9_-]/g, '_');
  function readRemovalQueue(owner) {
    const value = parse(localStorage.getItem(removalKey(owner)) || '[]', []);
    return new Set(Array.isArray(value) ? value.filter(item => typeof item === 'string') : []);
  }
  function writeRemovalQueue(owner, queue) {
    if (queue.size) localStorage.setItem(removalKey(owner), JSON.stringify([...queue]));
    else localStorage.removeItem(removalKey(owner));
  }

  async function clearReaddedRemovalMarkers(owner) {
    if (!owner) return;
    const queued = readRemovalQueue(owner);
    if (!queued.size) return;
    try {
      const local = await readCart();
      const current = readRemovalQueue(owner);
      let changed = false;
      for (const id of current) {
        // An item explicitly added back on this device cancels its old delete.
        const localLine = local.lines.get(id);
        if (localLine) {
          const baselineRaw = localStorage.getItem(ownerKey(owner));
          if (baselineRaw) {
            const baseline = baselineRead(owner);
            const baselineLines = indexLines(baseline.lines);
            const prior = baselineLines.get(id);
            if (prior && prior.quantity > 0) {
              // Mark the old baseline as removed so the re-add is recognized
              // as a newer local change, even when its quantity matches the
              // quantity that existed before the earlier removal.
              baselineLines.set(id, { ...prior, quantity: 0, updated_at: Date.now() });
              baselineWrite(owner, normalizeDocument({
                updated_at: Date.now(), lines: [...baselineLines.values()],
              }));
            }
          }
          current.delete(id);
          changed = true;
        }
      }
      if (changed) writeRemovalQueue(owner, current);
    } catch (error) {
      console.warn('[IJ Cart Account Sync] Could not check re-added cart lines.', error);
    }
  }

  function fromLocalCart(lines) {
    return new Map([...lines].map(([identity, line]) => [identity, { ...line, updated_at: 0 }]));
  }
function initialMerge(localLines, remoteLines, explicitRemovals = new Set()) {
  const merged = new Map();

  // Keep remote active lines and deletion tombstones. A remote tombstone
  // must beat an older copy of the same line on a device with no baseline.
  for (const [id, line] of remoteLines) {
    merged.set(id, { ...line });
  }

  for (const [id, local] of localLines) {
    if (explicitRemovals.has(id)) continue;

    const remote = merged.get(id);

    // Do not let an old local copy resurrect a line deleted on another device.
    if (remote && remote.quantity === 0) continue;

    merged.set(id, {
      ...local,
      quantity: Math.max(local.quantity, remote?.quantity || 0),
      updated_at: Date.now(),
    });
  }

  for (const id of explicitRemovals) {
    const template = merged.get(id) || localLines.get(id) || remoteLines.get(id);

    if (template) {
      merged.set(id, {
        ...template,
        quantity: 0,
        updated_at: template.quantity === 0
          ? template.updated_at
          : Math.max(Date.now(), template.updated_at || 0),
      });
    }
  }

  return merged;
}
  function reconcile(localLines, remoteLines, baseLines, explicitRemovals = new Set()) {
    const merged = new Map();
    const ids = new Set([...localLines.keys(), ...remoteLines.keys(), ...baseLines.keys()]);
    const remoteMax = Math.max(0, ...[...remoteLines.values()].map(line => line.updated_at));
    for (const id of ids) {
      const local = localLines.get(id) || null;
      const remote = remoteLines.get(id) || null;
      const base = baseLines.get(id) || null;
      // A line missing from this browser is not proof the shopper removed it.
      // Only cart.js's confirmed, explicit remove action can create a tombstone.
      if (explicitRemovals.has(id)) {
        const template = local || base || remote;
        if (template) merged.set(id, {
          ...template,
          quantity: 0,
          updated_at: remote?.quantity === 0
            ? remote.updated_at
            : Math.max(Date.now(), remoteMax + 1, (base?.updated_at || 0) + 1),
        });
        continue;
      }
      const localChanged = !!local && !sameLine(local, base);
      const remoteChanged = !sameLine(remote, base);
      if (localChanged) {
        merged.set(id, {
          ...local,
          quantity: local.quantity,
          updated_at: Math.max(Date.now(), remoteMax + 1, (base?.updated_at || 0) + 1),
        });
      } else if (remoteChanged && remote) {
        merged.set(id, { ...remote });
      } else if (remote) {
        merged.set(id, { ...remote });
      } else if (base) {
        // Preserve a tombstone until all active devices have observed it.
        merged.set(id, { ...base, quantity: 0 });
      }
    }
    return merged;
  }
  const activeLines = lines => [...lines.values()].filter(line => line.quantity > 0);
  const docsEqual = (a, b) => {
    const aa = [...indexLines(a.lines).values()].sort((x, y) => x.identity.localeCompare(y.identity));
    const bb = [...indexLines(b.lines).values()].sort((x, y) => x.identity.localeCompare(y.identity));
    return JSON.stringify(aa) === JSON.stringify(bb);
  };
  function toAddItem(line) {
    const item = { id: Number(line.variant_id), quantity: line.quantity };
    if (Object.keys(line.properties || {}).length) item.properties = line.properties;
    if (line.selling_plan) item.selling_plan = Number(line.selling_plan) || line.selling_plan;
    return item;
  }
  async function applyToLocalCore(lines, current, expectedMutationVersion) {
    const interrupted = () => pendingCartMutations > 0 ||
      (expectedMutationVersion !== undefined && cartMutationVersion !== expectedMutationVersion);
    if (interrupted()) return false;
    const desired = new Map(activeLines(lines).map(line => [line.identity, line]));
    const changes = [];
    const additions = [];
    for (const [id, present] of current.lines) {
      const wanted = desired.get(id);
      if (!wanted) changes.push({ id: present.key, quantity: 0 });
      else if (wanted.quantity !== present.quantity) changes.push({ id: present.key, quantity: wanted.quantity });
      desired.delete(id);
    }
    for (const change of changes) {
      if (interrupted()) return false;
      await postCart(CART_CHANGE_URL, change);
    }
    for (const line of desired.values()) additions.push(toAddItem(line));
    if (additions.length) {
      if (interrupted()) return false;
      await postCart(CART_ADD_URL, { items: additions });
    }
    if (interrupted()) return false;
if (changes.length || additions.length) {
  const after = await readCart();

  // A shopper change may have started while /cart.js was being read.
  if (interrupted()) return false;

  publishCartChanged(after.cart);
  return true;
}
    return true;
  }
  let localApplyInFlight = null;
  async function applyToLocal(lines, current, expectedMutationVersion) {
    const operation = applyToLocalCore(lines, current, expectedMutationVersion);
    localApplyInFlight = operation;
    try {
      return await operation;
    } finally {
      if (localApplyInFlight === operation) localApplyInFlight = null;
    }
  }
  async function waitForLocalApply() {
    while (localApplyInFlight) {
      try { await localApplyInFlight; } catch (_) {}
    }
  }
  function publishCartChanged(cartData) {
    muted = true;
    try {
      document.dispatchEvent(new Event('cartUpdated'));
      if (typeof window.publish === 'function') {
        window.publish('cart-update', { source: 'ij-cart-account-sync', cartData });
      }
    } finally {
      setTimeout(() => { muted = false; }, 400);
    }
  }

  async function syncOnceCore(options = {}) {
    const applyRemoteToLocal = options.applyRemoteToLocal === true;
    if (working || muted || pendingCartMutations || document.visibilityState === 'hidden') return;
    if (!CFG.customerSignedIn || !validSession()) return;
    const api = accountApi();
    if (!api?.graphql) return;
    working = true;
    const mutationVersionAtStart = cartMutationVersion;
    try {
      for (let attempt = 0; attempt < 3; attempt++) {
        // Read a fresh cart on every retry; the shopper may have changed it
        // while Shopify was returning a compareDigest conflict.
        const local = await readCart();
        const account = await readAccount();
        if (!account) return;
        const remote = indexLines(account.document.lines);
        const base = baselineRead(account.owner);
        const baseLines = indexLines(base.lines);
        const noBaseline = !localStorage.getItem(ownerKey(account.owner));
        const removals = readRemovalQueue(account.owner);
        const confirmedRemovals = new Set();
        for (const id of [...removals]) {
          const accountLine = remote.get(id);
// Clear a deletion marker only after a fresh account read confirms
// that the account already contains the zero-quantity tombstone.
// If the account line is absent, keep the marker so this sync can write it.
          if (accountLine && accountLine.quantity === 0) {
            removals.delete(id);
            confirmedRemovals.add(id);
          }
        }
        let merged;
        if (noBaseline) merged = initialMerge(fromLocalCart(local.lines), remote, removals);
        else merged = reconcile(fromLocalCart(local.lines), remote, baseLines, removals);

        const target = normalizeDocument({ updated_at: Date.now(), lines: [...merged.values()] });
        let canonical = account.document;
        if (!docsEqual(target, account.document)) {
          try {
            const saved = await writeAccount(account.owner, account.digest, target);
            canonical = saved.document;
          } catch (error) {
            const conflict =
              error?.conflict ||
              /compare.?digest|stale|conflict/i.test(String(error?.message || error || ''));

            if (conflict && attempt < 2) {
              const backoff = 250 * (attempt + 1) + Math.floor(Math.random() * 500);
              await new Promise(resolve => setTimeout(resolve, backoff));
              continue;
            }

            throw error;
          }
        }
        // Save remotely first. If Shopify rejects a stale compareDigest, the
        // local cart has not been touched and the merge can be retried safely.
        // Account writes are background-only. Apply an incoming merge to
        // Shopify's live cart only at a page-entry checkpoint, and only if no
        // customer cart action happened while the request was in flight.
        let localApplied = !applyRemoteToLocal;
        if (applyRemoteToLocal && !pendingCartMutations &&
            mutationVersionAtStart === cartMutationVersion) {
          localApplied = await applyToLocal(merged, local, mutationVersionAtStart);
        }
        // Baseline must describe what this device has actually adopted. If a
        // background poll advances it before applying remote quantities, a
        // later pass can mistake the older local cart for a new edit.
        if (localApplied && applyRemoteToLocal) baselineWrite(account.owner, canonical);
        const remainingRemovals = readRemovalQueue(account.owner);
        for (const id of confirmedRemovals) remainingRemovals.delete(id);
        writeRemovalQueue(account.owner, remainingRemovals);
        if (applyRemoteToLocal && (!localApplied || mutationVersionAtStart !== cartMutationVersion)) {
          schedule(150);
        }
        return;
      }
    } catch (error) {

      console.warn('[IJ Cart Account Sync]', error);
    } finally {
      working = false;
    }
  }
  async function syncOnce(options = {}) {
    if (options.applyRemoteToLocal === true) applyRemoteQueued = true;
    if (syncInFlight) {
      // Preserve a second pass requested during a cart mutation or checkpoint.
      syncAgain = true;
      return syncInFlight;
    }
    const applyRemoteToLocal = applyRemoteQueued;
    applyRemoteQueued = false;
    const operation = syncOnceCore({ applyRemoteToLocal });
    syncInFlight = operation;
    try {
      return await operation;
    } finally {
      if (syncInFlight === operation) syncInFlight = null;
      if (syncAgain) {
        syncAgain = false;
        const applyNext = applyRemoteQueued;
        applyRemoteQueued = false;
        schedule(450, { applyRemoteToLocal: applyNext });
      }
    }
  }
  async function waitForSyncIdle() {
    // Compatibility alias: callers wait only for an active account-to-cart
    // restore, never for a background account read or metafield save.
    await waitForLocalApply();
  }
  function schedule(delay = 450, options = {}) {
    if (options.applyRemoteToLocal === true) applyRemoteQueued = true;
    if (muted) return;
    clearTimeout(timer);
    timer = setTimeout(() => {
      const applyRemoteToLocal = applyRemoteQueued;
      applyRemoteQueued = false;
      syncOnce({ applyRemoteToLocal });
    }, delay);
  }
  function maybeAuthenticate() {
    const current = status();
    if (!CFG.customerSignedIn || validSession()) return;
    if (current.authenticated && Number(current.expiresAt) <= Date.now() + 30000) authAttempted = false;
    if (authAttempted) return;
    const previous = Number(sessionStorage.getItem(AUTH_ATTEMPT) || 0);
    if (previous && Date.now() - previous < 30 * 60 * 1000) return;
    const api = accountApi();
    if (typeof api?.connectSilently !== 'function') return;
    authAttempted = true;
    sessionStorage.setItem(AUTH_ATTEMPT, String(Date.now()));
    Promise.resolve(api.connectSilently()).catch(error => {
      console.warn('[IJ Cart Account Sync] Silent account check failed.', error);
    });
  }
  function start() {
    if (!CFG.enabled) return;
    const api = accountApi();
    accountWasAuthenticated = validSession();
    api?.subscribe?.(() => {
      const authenticated = validSession();
      const justConnected = authenticated && !accountWasAuthenticated;
      accountWasAuthenticated = authenticated;
      if (justConnected) schedule(150, { applyRemoteToLocal: true });
    });
    document.addEventListener('ij:cart-line-change-intent', () => {
      // A live cart action cancels a not-yet-started account restore.
      clearTimeout(timer);
      applyRemoteQueued = false;
      cartMutationVersion++;
      pendingCartMutations++;
    });
    document.addEventListener('ij:cart-line-change-failed', () => {
      cartMutationVersion++;
      pendingCartMutations = Math.max(0, pendingCartMutations - 1);
      schedule(100);
    });
document.addEventListener('ij:cart-line-change-committed', event => {
  cartMutationVersion++;
  pendingCartMutations = Math.max(0, pendingCartMutations - 1);

  const detail = event.detail || {};
  const qty = Math.max(0, Math.floor(Number(detail.quantity) || 0));
  const current = status();
  const expectedId = expectedCustomerId();
  const owner = current.owner || (expectedId ? `gid://shopify/Customer/${expectedId}` : '');

  if (
    qty === 0 &&
    CFG.customerSignedIn &&
    /^gid:\/\/shopify\/Customer\/\d+$/.test(owner) &&
    (!expectedId || owner.endsWith(`/${expectedId}`))
  ) {
    const line = cartLine({
      variant_id: detail.variant_id,
      quantity: 0,
      properties: detail.properties,
      selling_plan: detail.selling_plan,
    });

    if (line) {
      const removals = readRemovalQueue(owner);
      removals.add(line.identity);
      writeRemovalQueue(owner, removals);

      const baseline = baselineRead(owner);
      const baselineLines = indexLines(baseline.lines);
      const previous = baselineLines.get(line.identity);

      baselineLines.set(line.identity, {
        ...line,
        quantity: 0,
        updated_at: Math.max(Date.now(), (previous?.updated_at || 0) + 1),
      });

      baselineWrite(owner, normalizeDocument({
        updated_at: Date.now(),
        lines: [...baselineLines.values()],
      }));
    }
  }

  schedule(0);
});
    document.addEventListener('cartUpdated', () => {
      if (!muted) {
        cartMutationVersion++;
        const current = status();
        const expectedId = expectedCustomerId();
        const owner = current.owner || (expectedId ? `gid://shopify/Customer/${expectedId}` : '');
        clearReaddedRemovalMarkers(owner);
      }
      schedule();
    });
    window.addEventListener('storage', event => {
      if (event.key?.startsWith(REMOVAL_PREFIX)) schedule(0);
    });
    if (typeof window.subscribe === 'function') {
      window.subscribe('cart-update', event => {
        if (event?.source !== 'ij-cart-account-sync') {
          const current = status();
          const expectedId = expectedCustomerId();
          const owner = current.owner || (expectedId ? `gid://shopify/Customer/${expectedId}` : '');
          clearReaddedRemovalMarkers(owner);
          schedule();
        }
      });
    }
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') schedule(120);
    });
    // Every page entry is a safe account-to-cart checkpoint, including a
    // normal navigation after authentication finishes. Focus and visibility
    // checks remain background-only and never rewrite the live cart.
    window.addEventListener('pageshow', () => {
      schedule(120, { applyRemoteToLocal: true });
    });
    window.addEventListener('focus', () => schedule(120));
    poller = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (CFG.customerSignedIn && validSession()) schedule(0);
    }, POLL_MS);
    if (validSession()) schedule(300, { applyRemoteToLocal: true });
  }

  window.IJCartAccountSync = Object.freeze({ sync: syncOnce, waitForLocalApply, waitForSyncIdle });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
